import { System_DayOfWeek } from '../../api/models/System_DayOfWeek';

export interface DaySchedule {
  id?: string;
  day: string;
  shortDay: string;
  dayOfWeek: System_DayOfWeek;
  enabled: boolean;
  startTime: string;
  endTime: string;
  /** Not edited in the wizard, but round-tripped so it is never cleared. */
  lunchStartTime?: string | null;
  lunchEndTime?: string | null;
}

const weekday = (
  day: string,
  shortDay: string,
  dayOfWeek: System_DayOfWeek
): DaySchedule => ({
  day,
  shortDay,
  dayOfWeek,
  enabled: true,
  startTime: '08:00',
  endTime: '17:00',
  lunchStartTime: null,
  lunchEndTime: null,
});

const weekendDay = (
  day: string,
  shortDay: string,
  dayOfWeek: System_DayOfWeek
): DaySchedule => ({
  day,
  shortDay,
  dayOfWeek,
  enabled: false,
  startTime: '09:00',
  endTime: '14:00',
  lunchStartTime: null,
  lunchEndTime: null,
});

export const DEFAULT_SCHEDULE: DaySchedule[] = [
  weekday('Monday', 'Mon', System_DayOfWeek.MONDAY),
  weekday('Tuesday', 'Tue', System_DayOfWeek.TUESDAY),
  weekday('Wednesday', 'Wed', System_DayOfWeek.WEDNESDAY),
  weekday('Thursday', 'Thu', System_DayOfWeek.THURSDAY),
  weekday('Friday', 'Fri', System_DayOfWeek.FRIDAY),
  weekendDay('Saturday', 'Sat', System_DayOfWeek.SATURDAY),
  weekendDay('Sunday', 'Sun', System_DayOfWeek.SUNDAY),
];

export const SCHEDULE_PRESETS = [
  { label: 'Weekdays 8–5', days: [1, 2, 3, 4, 5], startTime: '08:00', endTime: '17:00' },
  { label: 'Weekdays 9–5', days: [1, 2, 3, 4, 5], startTime: '09:00', endTime: '17:00' },
  { label: 'Mon–Sat 8–5', days: [1, 2, 3, 4, 5, 6], startTime: '08:00', endTime: '17:00' },
] as const;

// Saturday and Sunday stay independent of the weekday sync group. Customers
// commonly run a shorter weekend shift, and reported weekday hours getting
// clobbered when Saturday was edited.
const WEEKEND_DAYS = new Set(['Saturday', 'Sunday']);

export const isWeekend = (day: string): boolean => WEEKEND_DAYS.has(day);

type SyncableDay = {
  day: string;
  enabled: boolean;
  startTime: string;
  endTime: string;
};

/**
 * Weekday sync: with sync on, changing one weekday's hours copies them to the
 * other enabled weekdays. Saturday and Sunday are always edited on their own,
 * and switching a day on or off never cascades to any other day.
 */
export function applyDayScheduleChange<T extends SyncableDay>(
  prev: T[],
  index: number,
  patch: Partial<T>,
  syncEnabled: boolean
): T[] {
  const old = prev[index];
  if (!old) return prev;
  const updated = { ...old, ...patch };

  if (!syncEnabled || old.enabled !== updated.enabled || isWeekend(updated.day)) {
    return prev.map((day, i) => (i === index ? updated : day));
  }

  const startChanged = old.startTime !== updated.startTime;
  const endChanged = old.endTime !== updated.endTime;

  if (!startChanged && !endChanged) {
    return prev.map((day, i) => (i === index ? updated : day));
  }

  return prev.map((day, i) => {
    if (i === index) return updated;
    if (!day.enabled || isWeekend(day.day)) return day;
    return {
      ...day,
      startTime: startChanged ? updated.startTime : day.startTime,
      endTime: endChanged ? updated.endTime : day.endTime,
    };
  });
}

/**
 * Existing schedules can have weekdays that already differ. Turning sync on in
 * that situation would flatten hours the user set deliberately, so it starts
 * off instead.
 */
export function shouldSyncWeekdays(schedule: SyncableDay[]): boolean {
  const distinct = new Set(
    schedule.filter(d => d.enabled && !isWeekend(d.day)).map(d => `${d.startTime}-${d.endTime}`)
  );
  return distinct.size <= 1;
}
